interface ValidationBannerProps {
  /** Number of distinct fields currently in error. */
  errorCount: number;
  /**
   * Optional non-field-specific error (cross-field rule violation, network
   * failure). Always rendered if present, regardless of `errorCount`. Use
   * this for genuinely-cross-field errors that wouldn't otherwise surface
   * inline on any single field.
   */
  topLevelMessage?: string | null;
}

/**
 * Form-validation summary. Suppresses itself for the trivial case so the
 * user isn't shown two error messages for one mistake:
 *
 * - 0 field errors AND no top-level message → renders nothing.
 * - 1 field error AND no top-level message → renders nothing
 *   (the inline error is enough).
 * - 2+ field errors → renders "N problems — fix the highlighted fields
 *   below."
 * - Any top-level message → renders it as a standalone banner, regardless
 *   of the field-error count.
 *
 * Mount this directly above the form. Pair every errored input with
 * `aria-invalid={true}` (UI-09) so screen-reader users get a coherent
 * picture from the banner + inline messages.
 */
export function ValidationBanner({ errorCount, topLevelMessage }: ValidationBannerProps) {
  if (topLevelMessage) {
    return (
      <div
        role="alert"
        className="rounded-md border border-rag-red/30 bg-rag-red-soft px-3 py-2 text-sm text-rag-red"
      >
        {topLevelMessage}
      </div>
    );
  }
  if (errorCount < 2) return null;
  return (
    <div
      role="alert"
      className="rounded-md border border-rag-red/30 bg-rag-red-soft px-3 py-2 text-sm text-rag-red"
    >
      {errorCount} problems — fix the highlighted fields below.
    </div>
  );
}
